// Bosla has suspended this restaurant's account (UC-23).
//
// Any request of the account can answer 403 ACCOUNT_SUSPENDED: pairing,
// unlocking, the menu check, a sale, any dashboard read. Rather than each
// caller looking for it, the API client hands every answer here, and the till
// and the dashboard read this one state: the till covers whatever screen it
// is on, and the dashboard shows only the suspended screen.
//
// It clears only when a route that checks suspension answers yes: the till's
// menu check, which is how the cover goes once Bosla resumes the account, the
// till's own question of which till it is, and the dashboard's restaurant. Any
// other answer says nothing either way: a public route, or one that never
// reaches an account, would clear it while the account is still suspended.
// The answer names whom to contact, never why: the reason is for Bosla's admins.

import type { Middleware } from 'openapi-fetch';

export interface Suspension {
	supportEmail: string;
	/** Empty when Bosla has no WhatsApp number. */
	supportWhatsapp: string;
}

let current = $state<Suspension | null>(null);

export function suspension(): Suspension | null {
	return current;
}

/** Forget it without asking, for a screen with nothing to ask with. */
export function clearSuspension(): void {
	current = null;
}

interface Refusal {
	code?: string;
	details?: { supportEmail?: unknown; supportWhatsapp?: unknown };
}

/** Reads a refusal's body, or null when it is not one this module knows. */
export function suspensionOf(status: number, body: unknown): Suspension | null {
	if (status !== 403 || typeof body !== 'object' || body === null) return null;
	const refusal = body as Refusal;
	if (refusal.code !== 'ACCOUNT_SUSPENDED') return null;
	const email = refusal.details?.supportEmail;
	const whatsapp = refusal.details?.supportWhatsapp;
	return {
		supportEmail: typeof email === 'string' ? email : '',
		supportWhatsapp: typeof whatsapp === 'string' ? whatsapp : '',
	};
}

/** The routes whose success proves the account is not suspended. */
const CHECKS = ['/api/till/menu', '/api/owner/menu', '/api/till/me', '/api/owner/accounts/me'];

/** Whether an answer from this URL with this status says the account is active. */
export function clearsSuspension(url: string, status: number): boolean {
	if (status >= 400) return false;
	// The path alone: the request URL carries the API's origin and maybe a query.
	const path = url.replace(/^[a-z]+:\/\/[^/]+/i, '').split(/[?#]/)[0] ?? '';
	return CHECKS.includes(path);
}

export const watchSuspension: Middleware = {
	async onResponse({ request, response }) {
		if (clearsSuspension(request.url, response.status)) {
			current = null;
			return;
		}
		if (response.status !== 403) return;
		try {
			const found = suspensionOf(response.status, await response.clone().json());
			if (found) current = found;
		} catch {
			// Not JSON: not this refusal.
		}
	},
};
